import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../App';
import { ga } from '../utils/ga4';
import { applySEO } from '../utils/seo';
import { Calendar, ArrowRight, Tag, Search, Loader2 } from 'lucide-react';

const PAW = 'https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/r89kksa2_Petfolk%20Paw%20Multi.png';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

export default function BlogListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    const path = category ? `/api/blog?category=${encodeURIComponent(category)}&limit=50` : '/api/blog?limit=50';
    apiFetch(path)
      .then(data => setBlogs(data.items || []))
      .catch(() => setBlogs([]))
      .finally(() => setLoading(false));
    ga.blogListView(category);
  }, [category]);

  useEffect(() => {
    apiFetch('/api/blog/categories').then(d => setCategories(d.categories || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!search) return blogs;
    const s = search.toLowerCase();
    return blogs.filter(b =>
      (b.title || '').toLowerCase().includes(s) ||
      (b.excerpt || '').toLowerCase().includes(s) ||
      (b.tags || []).some(t => t.toLowerCase().includes(s))
    );
  }, [blogs, search]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  const setCat = (c) => {
    if (c) setSearchParams({ category: c });
    else setSearchParams({});
  };

  const pageTitle = category ? `${category} — Petfolk Blog` : 'Petfolk Blog — Pet Care, Grooming Tips & Stories';
  const pageDesc = category
    ? `${category} articles by Petfolk: practical pet care, grooming guidance, and stories for Indian pet parents.`
    : 'Honest pet-care guidance from Petfolk — grooming tips, seasonal care, hygiene, adoption, and the emotional life of pets.';
  const canonical = 'https://petfolk.in/blog' + (category ? `?category=${encodeURIComponent(category)}` : '');

  // Inject SEO + JSON-LD Blog schema (helmet-async v2 unreliable for meta updates)
  useEffect(() => {
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Petfolk Blog',
      description: pageDesc,
      url: canonical,
      publisher: {
        '@type': 'Organization',
        name: 'Petfolk',
        logo: { '@type': 'ImageObject', url: 'https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/ykqrxquz_Petfolk%20Logo%20Color%20TransparentBG.png' },
      },
      blogPost: blogs.slice(0, 10).map(b => ({
        '@type': 'BlogPosting',
        headline: b.title,
        description: b.excerpt,
        datePublished: b.publishedAt,
        url: `https://petfolk.in/blog/${b.slug}`,
        image: b.coverImageUrl || undefined,
      })),
    };
    return applySEO({
      title: pageTitle,
      description: pageDesc,
      canonical,
      ogType: 'website',
      jsonLd: ld,
      jsonLdId: 'blog-list-jsonld',
    });
  }, [blogs, canonical, pageDesc, pageTitle]);

  return (
    <div className="relative min-h-screen pt-16" style={{ background: 'var(--bg-primary)' }} data-testid="blog-list-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 pt-10 pb-8 max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-petfolk-orange/10 text-petfolk-orange text-xs font-semibold uppercase tracking-wider">
              <img src={PAW} alt="" className="w-3.5 h-3.5" />
              Petfolk Blog
            </div>
            <h1 className="font-outfit font-bold leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl" style={{ color: 'var(--text-primary)' }}>
              Stories, tips & honest <span className="gradient-text">pet care</span>.
            </h1>
            <p className="mt-3 text-base sm:text-lg max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
              Practical guidance for Indian pet parents — grooming, seasonal care, hygiene, adoption, and the emotional life of pets.
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input
              data-testid="blog-public-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border bg-transparent text-sm focus:outline-none focus:border-petfolk-orange"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />
          </div>
        </div>

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="blog-categories">
            <button
              onClick={() => setCat('')}
              data-testid="blog-cat-all"
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${!category ? 'bg-petfolk-orange text-white' : 'border hover:bg-black/5 dark:hover:bg-white/5'}`}
              style={{ borderColor: !category ? 'transparent' : 'var(--border-color)', color: !category ? '#fff' : 'var(--text-secondary)' }}
            >
              All
            </button>
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${category === c ? 'bg-petfolk-orange text-white' : 'border hover:bg-black/5 dark:hover:bg-white/5'}`}
                style={{ borderColor: category === c ? 'transparent' : 'var(--border-color)', color: category === c ? '#fff' : 'var(--text-secondary)' }}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Content */}
      <section className="px-4 sm:px-6 pb-16 max-w-6xl mx-auto">
        {loading ? (
          <div className="py-20 text-center" data-testid="blog-loading"><Loader2 className="animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center" data-testid="blog-empty-public">
            <p className="text-lg font-outfit font-semibold" style={{ color: 'var(--text-primary)' }}>No posts yet.</p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>Check back soon for fresh stories from Petfolk.</p>
          </div>
        ) : (
          <>
            {/* Featured card */}
            {featured && (
              <Link to={`/blog/${featured.slug}`} className="group block mb-10 rounded-3xl overflow-hidden border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }} data-testid="blog-featured">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="aspect-[16/10] md:aspect-auto md:h-full overflow-hidden bg-gradient-to-br from-petfolk-orange/20 via-petfolk-purple/20 to-petfolk-teal/20 relative">
                    {featured.coverImageUrl ? (
                      <img src={featured.coverImageUrl} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <img src={PAW} alt="" className="w-24 h-24 opacity-50 group-hover:rotate-12 transition-transform" />
                      </div>
                    )}
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 text-petfolk-orange text-xs font-bold uppercase tracking-wider">Featured</span>
                  </div>
                  <div className="p-6 sm:p-8 flex flex-col justify-center">
                    {featured.category && (
                      <span className="inline-flex w-fit items-center gap-1 mb-3 text-xs font-semibold uppercase tracking-wider text-petfolk-orange">
                        <Tag size={12} /> {featured.category}
                      </span>
                    )}
                    <h2 className="font-outfit text-2xl sm:text-3xl font-bold leading-tight mb-3 group-hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-primary)' }}>
                      {featured.title}
                    </h2>
                    {featured.excerpt && (
                      <p className="text-sm sm:text-base line-clamp-3 mb-4" style={{ color: 'var(--text-secondary)' }}>{featured.excerpt}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {featured.publishedAt && (
                        <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {formatDate(featured.publishedAt)}</span>
                      )}
                      {featured.authorName && <span>· by {featured.authorName}</span>}
                    </div>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-petfolk-orange font-semibold text-sm">
                      Read article <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid */}
            {rest.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                {rest.map(b => (
                  <Link
                    key={b.id}
                    to={`/blog/${b.slug}`}
                    className="group rounded-2xl overflow-hidden border hover:shadow-xl hover:-translate-y-0.5 transition-all"
                    style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}
                    data-testid={`blog-card-${b.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-gradient-to-br from-petfolk-orange/15 via-petfolk-purple/15 to-petfolk-teal/15 relative">
                      {b.coverImageUrl ? (
                        <img src={b.coverImageUrl} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <img src={PAW} alt="" className="w-16 h-16 opacity-40 group-hover:rotate-12 transition-transform" />
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      {b.category && (
                        <span className="inline-flex w-fit items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-petfolk-orange mb-2">
                          {b.category}
                        </span>
                      )}
                      <h3 className="font-outfit text-lg font-bold leading-snug mb-2 group-hover:text-petfolk-orange transition-colors line-clamp-2" style={{ color: 'var(--text-primary)' }}>
                        {b.title}
                      </h3>
                      {b.excerpt && (
                        <p className="text-sm line-clamp-3 mb-3" style={{ color: 'var(--text-secondary)' }}>{b.excerpt}</p>
                      )}
                      <div className="flex items-center justify-between text-xs" style={{ color: 'var(--text-secondary)' }}>
                        <span className="inline-flex items-center gap-1"><Calendar size={11} /> {formatDate(b.publishedAt)}</span>
                        <span className="inline-flex items-center gap-1 text-petfolk-orange font-medium">Read <ArrowRight size={11} /></span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
